import {useState, useCallback, useMemo} from "react";
import {GridFilterModel, GridSortModel} from "@mui/x-data-grid-pro";
import {
	useListCohortAnnouncements,
	type ListCohortAnnouncementsParams,
	AnnouncementsControllerGetListOrderBy as OrderByFieldValues,
	type AnnouncementsControllerGetListOrderBy,
	AnnouncementsControllerGetListOrder as SortOrderValues,
	type AnnouncementsControllerGetListOrder
} from "@/app2/shared/data";
import {safeJsonParse} from "@/utils/common-utilities";
import {gridFilterModelToListParams} from "./gridFilterModelToListParams";

const VALID_ORDER_BY_FIELDS = new Set<string>(Object.values(OrderByFieldValues));

/** Per-tab persistence so sort/filter survive in-app navigation but reset on tab close. */
const SORT_STORAGE_KEY = "announcementsAdminListSortSettings";
const FILTER_STORAGE_KEY = "announcementsAdminListFilterModel";

interface PersistedSort {
	orderBy: AnnouncementsControllerGetListOrderBy;
	order: AnnouncementsControllerGetListOrder;
}

function readSessionItem<T>(key: string): T | undefined {
	if (typeof window === "undefined") return undefined;
	try {
		const raw = window.sessionStorage.getItem(key);
		return raw ? safeJsonParse<T>(raw) : undefined;
	} catch {
		return undefined;
	}
}

function writeSessionItem<T>(key: string, value: T): void {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Storage may be unavailable (private mode, quota); persistence is best-effort.
	}
}

function getPersistedSort(): PersistedSort | undefined {
	const persisted = readSessionItem<PersistedSort>(SORT_STORAGE_KEY);
	if (!persisted) return undefined;
	if (!VALID_ORDER_BY_FIELDS.has(persisted.orderBy)) return undefined;
	if (persisted.order !== "asc" && persisted.order !== "desc") return undefined;
	return persisted;
}

function getPersistedFilterModel(): GridFilterModel | undefined {
	const persisted = readSessionItem<GridFilterModel>(FILTER_STORAGE_KEY);
	if (!persisted || !Array.isArray(persisted.items)) return undefined;
	return persisted;
}

/** List query keys cleared before applying a new filter model (see {@link gridFilterModelToListParams}). */
const FILTER_PARAM_KEYS = [
	"searchTerm",
	"authorSearch",
	"authorSearchMode",
	"displayStatus",
	"displayStatusNot",
	"deliveryDateFrom",
	"deliveryDateTo",
	"readPercentMin",
	"readPercentMax",
	"titlePresence",
	"authorPresence",
	"deliveryPresence",
	"readPresence"
] as const satisfies readonly (keyof ListCohortAnnouncementsParams)[];

function gridFieldToOrderBy(field: string | undefined): AnnouncementsControllerGetListOrderBy {
	if (field && VALID_ORDER_BY_FIELDS.has(field)) {
		return field as AnnouncementsControllerGetListOrderBy;
	}
	return OrderByFieldValues.id;
}

function gridSortToOrder(sort: string | null | undefined): AnnouncementsControllerGetListOrder {
	return sort === "asc" || sort === "desc" ? sort : SortOrderValues.desc;
}

/** API allows `adminDisplayStatus`; the grid column field is `_status`. */
export function orderByToGridSortField(orderBy: string | undefined): string {
	if (!orderBy) return "id";
	if (orderBy === "adminDisplayStatus") return "_status";
	if (orderBy === "readDistribution") return "_read";
	return orderBy;
}

export const useAnnouncementsListGrid = () => {
	const initialPersistedFilterModel = useMemo(() => getPersistedFilterModel(), []);
	const [params, setParams] = useState<ListCohortAnnouncementsParams>(() => {
		const persistedSort = getPersistedSort();
		const baseParams: ListCohortAnnouncementsParams = {
			page: 1,
			pageSize: 25,
			orderBy: persistedSort?.orderBy ?? "id",
			order: persistedSort?.order ?? "desc"
		};
		if (initialPersistedFilterModel) {
			return {...baseParams, ...gridFilterModelToListParams(initialPersistedFilterModel)};
		}
		return baseParams;
	});
	const [filterModelSignature, setFilterModelSignature] = useState(() =>
		JSON.stringify(initialPersistedFilterModel?.items ?? [])
	);

	const {data, isPending, isFetching} = useListCohortAnnouncements(params, {
		filterModelSignature
	});

	const rows = data?.data ?? [];
	const rowCount = data?.totalCount ?? 0;
	const isInitialLoad = isPending && data === undefined;

	const onPageChange = useCallback((page: number) => {
		setParams((prev) => ({...prev, page: page + 1}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setParams((prev) => ({...prev, pageSize, page: 1}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
		writeSessionItem<GridFilterModel>(FILTER_STORAGE_KEY, {
			items: newFilterModel.items ?? [],
			linkOperator: newFilterModel.linkOperator
		});
		setFilterModelSignature(JSON.stringify(newFilterModel.items ?? []));
		setParams((prev) => {
			const updated: ListCohortAnnouncementsParams = {...prev, page: 1};
			for (const key of FILTER_PARAM_KEYS) {
				delete updated[key];
			}
			const fromGrid = gridFilterModelToListParams(newFilterModel);
			return {...updated, ...fromGrid};
		});
	}, []);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		const orderBy =
			sortModel.length > 0 ? gridFieldToOrderBy(sortModel[0].field) : OrderByFieldValues.id;
		const order =
			sortModel.length > 0 ? gridSortToOrder(sortModel[0].sort) : SortOrderValues.desc;
		writeSessionItem<PersistedSort>(SORT_STORAGE_KEY, {orderBy, order});
		setParams((prev) => ({
			...prev,
			page: 1,
			orderBy,
			order
		}));
	}, []);

	// Controlled sort model must match server params; otherwise internal grid state desyncs
	// and pagination can crash (e.g. scrollToIndexes reading undefined refs — MUI X v5).
	const sortModel: GridSortModel = useMemo(() => {
		const field = orderByToGridSortField(params.orderBy);
		const sort = params.order === "asc" || params.order === "desc" ? params.order : "desc";
		return [{field, sort}];
	}, [params.orderBy, params.order]);

	return {
		rows,
		rowCount,
		isInitialLoad,
		isFetching,
		page: (params.page ?? 1) - 1,
		pageSize: params.pageSize ?? 25,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		sortModel,
		initialFilterModel: initialPersistedFilterModel
	};
};
